import {observable, action, computed} from 'mobx';
import {ITsCanvasAnnotateService} from '../../services/ts-canvas-annotate/ts-canvas-annotate.service';
import EventEmitter from 'events';
export interface ITsImageEditToolsController {
    zoomFactor: number;
    changeZoom(): void;
    handleZoomInput(value: string): void;
    wheelChangeZoom(event: WheelEvent, delta: number): void;
    handleSliderClick(event: React.MouseEvent<HTMLDivElement>): void;
    cancel(): void;
    undo(): void;
    scan(): void;
    selectedScanMode(): void;
    rotateLeft(): void;
    rotateRight(): void;
    zoomEnabled: boolean;
    hasAnnotations: boolean;
    scanCaptureToggleEnabled: boolean;
    rightRotateEnabled: boolean;
    leftRotateEnabled: boolean;
    isZoomDropdownOpen: boolean;
    config: ITsImageEditToolsConfig;
    on(event: string, cb: () => void): void;
    dispose(): void;
    toggleZoomDropdown(): void;
}

export interface ITsImageEditToolsConfig {
    showScanners: boolean;
    imageMode: string;
    useNewInterface: boolean;
    scanCaptureDesignEnabled: boolean;
}

export default class TsImageEditToolsController implements ITsImageEditToolsController {
    @observable public zoomFactor: number = 1;
    @observable public isZoomDropdownOpen: boolean = false;
    @observable public hasAnnotations: boolean = false;

    public emitter = new EventEmitter();
    private annotateService: any;

    constructor(
        annotateService: ITsCanvasAnnotateService,
        public config: ITsImageEditToolsConfig
    ) {
        this.annotateService = annotateService;

        this.annotateService?.on?.('zoom:change', this.changeZoomByService);
        this.annotateService?.on?.('annotation:change', this.changeAnnotationByService);
    }

    get rightRotateEnabled(): boolean {
        return this.annotateService.rightRotateEnabled;
    }

    get leftRotateEnabled(): boolean {
        return this.annotateService.leftRotateEnabled;
    }

    get scanCaptureToggleEnabled(): boolean {
        return this.config.scanCaptureDesignEnabled;
    }

    get zoomEnabled(): boolean {
        return this.annotateService.zoomEnabled;
    }

    @action
    changeAnnotationByService = () => {
        this.hasAnnotations = this.annotateService?.isAnnotated();
    };

    @action
    handleSliderClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        const container = event.currentTarget;
        const rect = container.getBoundingClientRect();
        // Calculate percentage clicked (accounting for vertical rotation)
        const clickY = event.clientY - rect.top;
        const percentage = clickY / rect.height;
        // Convert to zoom value (1-5 range)
        const newZoom = 1 + percentage * 4;
        this.zoomFactor = Math.max(1, Math.min(5, Math.round(newZoom * 10) / 10));
        this.changeZoom();
    };

    @action
    toggleZoomDropdown = (): void => {
        this.isZoomDropdownOpen = !this.isZoomDropdownOpen;
    };

    @action
    changeZoom = (): void => {
        this.annotateService?.setZoom(this.zoomFactor);
    };

    @action
    handleZoomInput = (value: string): void => {
        this.zoomFactor = parseFloat(value);
        this.changeZoom();
    };

    @action
    wheelChangeZoom = (event: WheelEvent, delta: number): void => {
        const newZoom = Math.max(1, Math.min(5, this.zoomFactor + (delta > 0 ? 0.1 : -0.1)));
        this.zoomFactor = Math.round(newZoom * 10) / 10;
        this.changeZoom();
    };

    @action
    changeZoomByService = (zoom: number): void => {
        this.zoomFactor = zoom;
    };

    rotateLeft = (): void => {
        this.annotateService.rotateLeft();
    };

    rotateRight = (): void => {
        this.annotateService.rotateRight();
    };

    cancel = (): void => {
        this.emitter.emit('cancel');
    };

    undo = (): void => {
        this.annotateService?.undo();
    };

    scan = (): void => {
        this.emitter.emit('scan');
    };

    selectedScanMode = (): void => {
        this.emitter.emit('selectedScanMode');
    };

    on(event: string, cb: () => void): void {
        this.emitter.on(event, cb);
    }

    dispose(): void {
        this.annotateService?.off?.('zoom:change', this.changeZoomByService);
        this.annotateService?.off?.('annotation:change', this.changeAnnotationByService);
        this.emitter.removeAllListeners();
    }
}
